/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Import component UI form cho Sổ Chi Tiết Đầu Tư Chứng Khoán (S45-DN)\nimport SecurityInvestmentLedgerDetail from "./info";
import SecurityInvestmentLedgerDetail from "./info";
import { SecuritiesPortfolioService } from "services/securities-portfolio";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

// 1. Initial values khớp DTO CreateValueAddedTaxRefundDto
const initialValues = {
  companyAddress: "",
  account: "",
  securityType: "",
  issuer: "",
  parValue: "",
  interestRate: "",
  paymentTerm: "",
  openingBalance: { quantity: "", amount: "" },
  items: [],
  totalArisingBought: { quantity: "", amount: "" },
  totalArisingSold: { quantity: "", amount: "" },
  closingBalance: { quantity: "", amount: "" },
  totalPages: "",
  openingDate: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
  preparerName: "",
  chiefAccountantName: "",
  legalRepresentativeName: "",
  status: "OPEN",
};

function SecuritiesPortfolioDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [data, setData] = useState({});

  const [formInitials, setFormInitials] = useState(initialValues);

  // 2. Load data từ API và map vào form
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await SecuritiesPortfolioService.getById(id);
        const apiData = response.data ? response.data : response;

        // Map items đúng schema SecuritiesPortfolio
        const mappedItems = (apiData.items || []).map((item) => ({
          recordingDate: item.recordingDate
            ? new Date(item.recordingDate).toLocaleDateString("vi-VN")
            : "",
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate
            ? new Date(item.documentDate).toLocaleDateString("vi-VN")
            : "",
          description: item.description || "",
          correspondingAccount: item.correspondingAccount || "",
          bought: {
            quantity: item.bought?.quantity || "",
            amount: item.bought?.amount || "",
          },
          sold: {
            quantity: item.sold?.quantity || "",
            amount: item.sold?.amount || "",
          },
          balance: {
            quantity: item.balance?.quantity || "",
            amount: item.balance?.amount || "",
          },
        }));

        const loadedData = {
          ...initialValues,
          company: apiData.company?.name || apiData.company || "",
          companyAddress: apiData.companyAddress || "",
          account: apiData.account || "",
          securityType: apiData.securityType || "",
          issuer: apiData.issuer || "",
          parValue: apiData.parValue || "",
          interestRate: apiData.interestRate || "",
          paymentTerm: apiData.paymentTerm || "",
          openingBalance: {
            quantity: apiData.openingBalance?.quantity || "",
            amount: apiData.openingBalance?.amount || "",
          },
          items: mappedItems.length > 0 ? mappedItems : [],
          totalArisingBought: {
            quantity: apiData.totalArisingBought?.quantity || "",
            amount: apiData.totalArisingBought?.amount || "",
          },
          totalArisingSold: {
            quantity: apiData.totalArisingSold?.quantity || "",
            amount: apiData.totalArisingSold?.amount || "",
          },
          closingBalance: {
            quantity: apiData.closingBalance?.quantity || "",
            amount: apiData.closingBalance?.amount || "",
          },
          totalPages: apiData.totalPages || "",
          openingDate: apiData.openingDate
            ? new Date(apiData.openingDate).toLocaleDateString("vi-VN")
            : "",
          signingDay: apiData.signingDay?.toString() || initialValues.signingDay,
          signingMonth: apiData.signingMonth?.toString() || initialValues.signingMonth,
          signingYear: apiData.signingYear?.toString() || initialValues.signingYear,
          preparerName: apiData.preparerName || "",
          chiefAccountantName: apiData.chiefAccountantName || "",
          legalRepresentativeName: apiData.legalRepresentativeName || "",
          status: apiData.status || "OPEN",
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi getById Sổ Chi Tiết Đầu Tư Chứng Khoán (S45-DN):", error);
        toast.error("Không thể tải dữ liệu Sổ Chi Tiết Đầu Tư Chứng Khoán");
      }
    })();
  }, [id]);

  const onChangeValue = useCallback((type, value) => {
    setData((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleBack = () => navigation(-1);

  // 3. Submit payload 100% khớp CreateValueAddedTaxRefundDto
  const submitForm = async (values) => {
    try {
      const num = (val) => (val && !isNaN(val) ? Number(val) : 0);

      const getValue = (field) => (data[field] !== undefined ? data[field] : values[field]);
      const itemsFlat = getValue("items") || [];

      // Build items theo SecuritiesPortfolio schema (SecurityTransaction[])
      const buildItems = (itemsList) =>
        itemsList.map((item) => ({
          recordingDate: item.recordingDate ? new Date(item.recordingDate) : null,
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate ? new Date(item.documentDate) : null,
          description: item.description || "",
          correspondingAccount: item.correspondingAccount || "",
          bought: {
            quantity: num(item.bought?.quantity),
            amount: num(item.bought?.amount),
          },
          sold: {
            quantity: num(item.sold?.quantity),
            amount: num(item.sold?.amount),
          },
          balance: {
            quantity: num(item.balance?.quantity),
            amount: num(item.balance?.amount),
          },
        }));

      // Build signingDate ISO nếu có
      const signD = getValue("signingDay");
      const signM = getValue("signingMonth");
      const signY = getValue("signingYear");
      let signingDate = null;
      if (signY && signM && signD) {
        signingDate = new Date(
          `${signY}-${String(signM).padStart(2, "0")}-${String(signD).padStart(2, "0")}`
        ).toISOString();
      }

      // Payload đúng theo CreateSecuritiesPortfolioDto
      const payload = {
        companyAddress: getValue("companyAddress") || "",
        account: getValue("account") || "",
        securityType: getValue("securityType") || "",
        issuer: getValue("issuer") || "",
        parValue: num(getValue("parValue")),
        interestRate: getValue("interestRate") || "",
        paymentTerm: getValue("paymentTerm") || "",
        openingBalance: {
          quantity: num(getValue("openingBalance")?.quantity),
          amount: num(getValue("openingBalance")?.amount),
        },
        items: buildItems(itemsFlat),
        totalArisingBought: {
          quantity: num(getValue("totalArisingBought")?.quantity),
          amount: num(getValue("totalArisingBought")?.amount),
        },
        totalArisingSold: {
          quantity: num(getValue("totalArisingSold")?.quantity),
          amount: num(getValue("totalArisingSold")?.amount),
        },
        closingBalance: {
          quantity: num(getValue("closingBalance")?.quantity),
          amount: num(getValue("closingBalance")?.amount),
        },
        totalPages: num(getValue("totalPages")),
        openingDate: getValue("openingDate") ? new Date(getValue("openingDate")) : null,
        signingDate: signingDate,
        preparerName: getValue("preparerName") || "",
        chiefAccountantName: getValue("chiefAccountantName") || "",
        legalRepresentativeName: getValue("legalRepresentativeName") || "",
        status: getValue("status") || "OPEN",
      };

      console.log("✅ SecuritiesPortfolio Payload:", JSON.stringify(payload, null, 2));

      if (!id) {
        await SecuritiesPortfolioService.create(payload);
        toast.success("Tạo Sổ Chi Tiết Đầu Tư Chứng Khoán thành công!");
      } else {
        await SecuritiesPortfolioService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
    } catch (error) {
      console.error("Lỗi lưu Sổ Chi Tiết Đầu Tư Chứng Khoán:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = (values) => {
    const finalData = { ...values, ...data };
    const htmlContent = getPrintTemplate({
      headerInfo: finalData,
      rows: finalData.items || [],
      openingBalance: finalData.openingBalance,
      totalArisingBought: finalData.totalArisingBought,
      totalArisingSold: finalData.totalArisingSold,
      closingBalance: finalData.closingBalance,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSubmit = async (values, actions) => {
    try {
      await submitForm(values);
    } catch (error) {
      // Error đã handle trong submitForm
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <SecurityInvestmentLedgerDetail
                          formData={{
                            values,
                            onChangeValue,
                          }}
                        />

                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Sổ Chi Tiết Đầu Tư Chứng Khoán (S45-DN)
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu" : "Tạo"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default SecuritiesPortfolioDetail;
